import { BMText } from "@traveloka/district-bloom";
import { ComponentProps } from "react";

type Segment =
  | { type: "text"; content: string }
  | { type: "bold"; content: string }
  | { type: "italic"; content: string }
  | { type: "underline"; content: string }
  | { type: "link"; content: string; href: string };

type Props = {
  markdown: string;
  handleLinkPress?: (href: string) => void;
} & ComponentProps<typeof BMText>;

export default function SimpleMarkdown({
  markdown,
  handleLinkPress,
  variant,
  ...props
}: Props) {
  const segments = parseMarkdown(markdown);
  return (
    <BMText {...props} variant={variant}>
      {segments.map((segment, index) => {
        switch (segment.type) {
          case "text":
            return segment.content;
          case "bold":
            return (
              <BMText
                key={index}
                variant={variant}
                style={{ fontWeight: "bold" }}
              >
                {segment.content}
              </BMText>
            );
          case "italic":
            return (
              <BMText
                key={index}
                variant={variant}
                style={{ fontStyle: "italic" }}
              >
                {segment.content}
              </BMText>
            );
          case "underline":
            return (
              <BMText
                key={index}
                variant={variant}
                style={{ textDecorationLine: "underline" }}
              >
                {segment.content}
              </BMText>
            );
          case "link":
            return (
              <BMText
                key={index}
                variant={variant}
                style={{ color: "blue" }}
                onPress={() => {
                  if (!handleLinkPress && typeof window !== "undefined") {
                    window.open(segment.href, "_blank");
                    return;
                  }
                  handleLinkPress?.(segment.href);
                }}
              >
                {segment.content}
              </BMText>
            );
          default:
            return null;
        }
      })}
    </BMText>
  );
}

function parseMarkdown(markdown: string): Segment[] {
  const regex =
    /(\*\*(.*?)\*\*)|(__([^_]+)__)|(_([^_]+)_)|(\[([^\]]+)\]\(([^)]+)\))/g;

  const segments: Segment[] = [];
  let lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = regex.exec(markdown)) !== null) {
    // Push plain text before match
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: markdown.slice(lastIndex, match.index),
      });
    }

    if (match[1]) {
      segments.push({ type: "bold", content: match[2] });
    } else if (match[3]) {
      segments.push({ type: "underline", content: match[4] });
    } else if (match[5]) {
      segments.push({ type: "italic", content: match[6] });
    } else if (match[7]) {
      segments.push({
        type: "link",
        content: match[8],
        href: match[9],
      });
    }

    lastIndex = regex.lastIndex;
  }

  // Push remaining plain text
  if (lastIndex < markdown.length) {
    segments.push({
      type: "text",
      content: markdown.slice(lastIndex),
    });
  }

  return segments;
}
